/**
 * Печать ленты чека через скрытый iframe: работает на любом принтере
 * из браузера и не блокируется как попап. Когда появится агент
 * устройства — эти же строки уйдут в ESC/POS-байты (см. план, срез 4).
 */
import { escposEncode, escposOpenDrawer } from '@dastarhan/core';

const AGENT_LS = 'dastarhan.printer.agent';

export function getAgentUrl(): string | null {
  return localStorage.getItem(AGENT_LS);
}
export function setAgentUrl(url: string | null): void {
  if (url) localStorage.setItem(AGENT_LS, url);
  else localStorage.removeItem(AGENT_LS);
}

const b64 = (bytes: Uint8Array) => {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
};

/** Печать через локального агента (ESC/POS в термопринтер). */
/** Молчал ли агент на прошлой проверке — предупреждение до печати. */
export function agentWasSilent(): boolean {
  return localStorage.getItem('dastarhan.printer.lastPing') === 'fail';
}

/**
 * Настольная версия кассы: печать идёт через саму программу.
 *
 * В exe агента нет вовсе — байты уходят в принтер из того же процесса,
 * что показывает кассу. Значит нечего ставить, нечего прописывать
 * в автозапуск и нечему упасть отдельно.
 */
type DesktopBridge = {
  isDesktop: true;
  print: (base64: string, which?: string) => Promise<{ ok: boolean; error?: string }>;
  printers?: () => Promise<string[]>;
  getSettings?: () => Promise<DeviceSettings>;
  saveSettings?: (next: Partial<DeviceSettings>) => Promise<{ ok: boolean }>;
  minimize?: () => Promise<void>;
  quit?: () => Promise<void>;
  onUpdateState?: (cb: (p: UpdateState) => void) => () => void;
  updateDownload?: () => Promise<void>;
  updateInstall?: () => Promise<void>;
};

/** Состояние автообновления, которое присылает оболочка. */
export type UpdateState =
  | { state: 'available'; version: string }
  | { state: 'downloading'; percent: number }
  | { state: 'ready'; version: string }
  | { state: 'error'; message: string };

/** Настройки, живущие у оболочки (settings.json рядом с программой). */
export type DeviceSettings = {
  serverUrl?: string;
  printerName?: string;
  kitchenPrinter?: string;
  barPrinter?: string;
};

const desktop = (): DesktopBridge | null =>
  (window as unknown as { dastarhan?: DesktopBridge }).dastarhan ?? null;

/** Есть ли настольный мост — по нему касса прячет подсказки про агента. */
export const isDesktopApp = (): boolean => desktop() !== null;

/** Системные принтеры Windows — для выбора в настройках exe. */
export async function desktopPrinters(): Promise<string[]> {
  return (await desktop()?.printers?.()) ?? [];
}

export async function desktopGetSettings(): Promise<DeviceSettings> {
  return (await desktop()?.getSettings?.()) ?? {};
}

export async function desktopSaveSettings(next: Partial<DeviceSettings>): Promise<void> {
  await desktop()?.saveSettings?.(next);
}

/*
 * Свернуть и закрыть — с экрана, пальцем.
 *
 * Касса живёт во весь экран без рамки окна, а на планшете нет
 * ни Alt+F4, ни крестика. Без этих двух вызовов человек заперт
 * в кассе и выключает планшет кнопкой питания — вместе с очередью
 * несохранённых привычек Windows.
 */
export function minimizeApp(): void {
  void desktop()?.minimize?.();
}

export function quitApp(): void {
  // Закрытие идёт через оболочку: она спросит про неотправленные чеки
  void desktop()?.quit?.();
}

/** Подписка на состояние обновления; в браузере — тихий холостой ход. */
export function onUpdateState(cb: (p: UpdateState) => void): () => void {
  return desktop()?.onUpdateState?.(cb) ?? (() => undefined);
}
export function updateDownload(): void { void desktop()?.updateDownload?.(); }
export function updateInstall(): void { void desktop()?.updateInstall?.(); }

const toBase64 = (bytes: Uint8Array): string => {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
};

export async function printViaAgent(bytes: Uint8Array, which?: string): Promise<boolean> {
  // Настольная версия печатает сама и не спрашивает про агента
  const d = desktop();
  if (d) {
    const res = await d.print(toBase64(bytes), which);
    return res.ok;
  }
  const url = getAgentUrl();
  if (!url) return false;
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 4000);
    const res = await fetch(`${url.replace(/\/$/, '')}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: b64(bytes) }),
      signal: ctrl.signal,
    });
    clearTimeout(timer);
    return res.ok;
  } catch {
    return false;
  }
}

/** Агент → термопринтер с QR; нет агента или он упал → печать браузера. */
/** Жив ли агент печати: короткий пинг с таймаутом — «настроено»
 *  и «работает» это разные вещи, и кассир вправе знать какая. */
export async function pingAgent(): Promise<boolean> {
  const url = localStorage.getItem(AGENT_LS);
  if (!url) return false;
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), 1500);
  try {
    const res = await fetch(`${url.replace(/\/$/, '')}/health`, { signal: ctrl.signal });
    localStorage.setItem('dastarhan.printer.lastPing', res.ok ? 'ok' : 'fail');
    return res.ok;
  } catch {
    localStorage.setItem('dastarhan.printer.lastPing', 'fail');
    return false;
  } finally {
    window.clearTimeout(t);
  }
}

/**
 * Печать с копиями.
 *
 * Копии шлём ОДНОЙ посылкой: между двумя отдельными запросами принтер
 * успевает получить чужое задание, и чеки разъедутся — гостю уйдёт
 * половина одного и половина другого.
 *
 * Отрез идёт после каждой копии, поэтому кассир снимает готовые
 * листы, а не разрывает ленту руками.
 */
export async function printSmart(lines: string[], qr?: string, copies?: number): Promise<void> {
  const n = copies ?? receiptCopies();
  const one = escposEncode(lines, qr ? { qr } : undefined);

  let bytes = one;
  if (n > 1) {
    const all = new Uint8Array(one.length * n);
    for (let i = 0; i < n; i++) all.set(one, one.length * i);
    bytes = all;
  }

  const ok = await printViaAgent(bytes);
  // Без агента печатаем через браузер — там копии задаёт сам диалог,
  // и вторую кассир поставит галочкой, если понадобится
  if (!ok) printReceipt(lines);
}

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export function printReceipt(lines: string[]): void {
  try {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(iframe);

    const doc = iframe.contentDocument;
    if (!doc) { iframe.remove(); return; }
    doc.open();
    doc.write(`<!doctype html><html lang="ru"><head><meta charset="utf-8"><style>
      @page { margin: 4mm; }
      body { margin: 0; }
      pre { font: 12px/1.4 "JetBrains Mono", ui-monospace, Consolas, monospace; margin: 0; white-space: pre; }
    </style></head><body><pre>${lines.map(esc).join('\n')}</pre></body></html>`);
    doc.close();

    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => iframe.remove(), 5000);
  } catch {
    // Печать никогда не роняет кассу: нет принтера — чек всё равно закрыт
  }
}

/** Вес с весов через того же агента, что и принтер. */
export async function readScale(): Promise<number | null> {
  const url = getAgentUrl();
  if (!url) return null;
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 3000);
    const res = await fetch(`${url.replace(/\/$/, '')}/scale/weight`, { signal: ctrl.signal });
    clearTimeout(timer);
    const data = (await res.json()) as { ok: boolean; kg?: number };
    return data.ok && typeof data.kg === 'number' ? data.kg : null;
  } catch {
    return null;
  }
}

/** Бегунок на конкретный агент цеха; свой URL — своя лента. */
export async function printRunner(lines: string[], agentUrl: string, which?: string): Promise<boolean> {
  // В программе для Windows бегунок уходит через мост на принтер
  // цеха, выбранный в настройках; http-агенты остаются браузерной
  // кассе. Иначе кухня в exe молчала бы при живом принтере
  const d = desktop();
  if (d) {
    const res = await d.print(toBase64(escposEncode(lines)), which ?? 'kitchen');
    return res.ok;
  }
  try {
    const res = await fetch(`${agentUrl.replace(/\/$/, '')}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: b64(escposEncode(lines)) }),
      signal: AbortSignal.timeout(4000),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Открыть денежный ящик без продажи.
 *
 * Нужно для размена и инкассации: кассир берёт мелочь или сдаёт
 * выручку, а чека при этом нет. Без этой команды ящик приходится
 * открывать ключом, и тогда операция нигде не отражается.
 *
 * Работает только через агента печати: браузер к принтеру напрямую
 * не ходит. Если агента нет — честно говорим, а не молчим.
 */
export async function openCashDrawer(): Promise<boolean> {
  return printViaAgent(escposOpenDrawer());
}

/**
 * Ширина ленты в символах.
 *
 * Два размера покрывают почти все кассовые принтеры:
 *  — 58 мм даёт 32 символа: узкие мобильные и старые модели;
 *  — 80 мм даёт 48: XP-80C, XP-80, большинство стационарных.
 *
 * Ошибка в этой цифре не ломает печать, а делает её некрасивой:
 * на 80-миллиметровой бумаге чек шириной 32 символа занимает
 * две трети листа, а колонка с ценой уезжает к середине.
 *
 * Храним на устройстве: у одного заведения бывает узкий принтер
 * на баре и широкий на кассе.
 */
export type PaperWidth = 32 | 48;

export function paperWidth(): PaperWidth {
  const raw = localStorage.getItem('dastarhan.paper');
  return raw === '48' ? 48 : raw === '32' ? 32 : 48;   // 80 мм — самый частый
}

export function setPaperWidth(w: PaperWidth): void {
  localStorage.setItem('dastarhan.paper', String(w));
}

/**
 * Сколько копий печатать.
 *
 * Одна гостю, вторая заведению — обычная практика: на второй
 * кассир ставит подпись и кладёт в ящик. Кто-то печатает три,
 * кто-то одну; настройка живёт на устройстве.
 */
export function receiptCopies(): number {
  const raw = Number(localStorage.getItem('dastarhan.copies') ?? '1');
  return Number.isFinite(raw) && raw >= 1 && raw <= 5 ? Math.floor(raw) : 1;
}

export function setReceiptCopies(n: number): void {
  localStorage.setItem('dastarhan.copies', String(Math.min(5, Math.max(1, n))));
}

/*
 * Виртуальные принтеры Windows (PDF, XPS, OneNote, факс) не понимают
 * сырые ESC/POS-байты: очередь принимает задание «успешно», а файл
 * выходит пустым. Такие ленты честнее показать на экране — и оттуда
 * при желании напечатать документом через диалог браузера.
 */
export async function isVirtualPrinter(): Promise<boolean> {
  try {
    const s = await (window as unknown as {
      dastarhan?: { getSettings?: () => Promise<{ printerName?: string }> };
    }).dastarhan?.getSettings?.();
    const name = s?.printerName ?? '';
    /*
     * Пустое имя — принтер чеков не выбран: сырьё улетело бы
     * в «принтер по умолчанию» Windows, а это лотерея (часто PDF).
     * Для отчётов честнее экранная лента; чеки этим детектором
     * не пользуются и идут прежним путём.
     */
    if (name.trim() === '') return true;
    return /pdf|xps|onenote|fax/i.test(name);
  } catch { return false; }
}
